import classNames from 'classnames';
import React, { useContext, PropsWithChildren, ComponentProps, forwardRef } from 'react';

// eslint-disable-next-line css-modules/no-unused-class
import globalStyles from '../index.module.css';
import { ElementTypes } from '../util/types';

export type UIStyle = 'default' | 'highlight';

const UIStyleContext = React.createContext<UIStyle>('default');

export function UIStyleContextProvider({
  children,
  uiStyle,
}: PropsWithChildren<{ uiStyle: UIStyle }>) {
  return <UIStyleContext.Provider value={uiStyle}>{children}</UIStyleContext.Provider>;
}

interface OwnProps<E extends ElementTypes> {
  element?: E;
  uiStyle: UIStyle;
}

type Props<E extends ElementTypes> = PropsWithChildren<
  OwnProps<E> & Omit<ComponentProps<E>, keyof OwnProps<E>>
>;

export const UIStyleContainer = forwardRef(
  <E extends ElementTypes>(
    { children, uiStyle, element, className, ...restProps }: Omit<Props<E>, 'ref'>,
    ref: React.Ref<HTMLElement>,
  ) => {
    const Element: ElementTypes = element ?? 'div';
    return (
      <UIStyleContextProvider uiStyle={uiStyle}>
        <Element
          className={classNames(className, {
            // eslint-disable-next-line css-modules/no-undef-class
            [globalStyles.defaultStyle]: uiStyle === 'default',
            // eslint-disable-next-line css-modules/no-undef-class
            [globalStyles.highlightStyle]: uiStyle === 'highlight',
          })}
          ref={ref}
          {...restProps}
        >
          {children}
        </Element>
      </UIStyleContextProvider>
    );
  },
);

export function useUIStyle() {
  return useContext(UIStyleContext);
}
